import React, { useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import { Alert, Button, Card, Spinner } from "react-bootstrap";
import { fetchClosed } from "../api/ethApi";
import { fmtDate, fmtNum } from "./tableUtils";

export default function ClosedTable() {
    const [rows, setRows] = useState([]);
    const [meta, setMeta] = useState({ total: 0, per_page: 10, current_page: 1 });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const columns = useMemo(
        () => [
            { name: "Grid", selector: (r) => r.buy_grid, sortable: true, right: true, width: "90px" },
            {
                name: "Profit",
                sortable: true,
                right: true,
                width: "110px",
                cell: (r) => <span className="font-monospace badge bg-success">{fmtNum(r.capital_received - r.capital, { digits: 6 })}</span>
            },
            {
                name: "Closed",
                selector: (r) => r.closed_at,
                sortable: true,
                cell: (r) => <span className="text-nowrap">{fmtDate(r.closed_at)}</span>
            },
            {
                name: "Open Date",
                selector: (r) => r.created_at,
                sortable: true,
                cell: (r) => <span className="text-nowrap">{fmtDate(r.created_at)}</span>
            },
            { name: "Buy Price", selector: (r) => r.buy_price, sortable: true, right: true },
            { name: "Sell Price", selector: (r) => r.sell_price, sortable: true, right: true },
            {
                name: "Capital",
                selector: (r) => r.capital,
                sortable: true,
                right: true,
                cell: (r) => <span className="font-monospace">{fmtNum(r.capital, { digits: 6 })}</span>
            },
            {
                name: "Capital Received",
                selector: (r) => r.capital_received,
                sortable: true,
                right: true,
                cell: (r) => <span className="font-monospace">{fmtNum(r.capital_received, { digits: 6 })}</span>
            },
        ],
        []
    );

    const load = useCallback(async ({ page = meta.current_page, perPage = meta.per_page } = {}) => {
        setLoading(true);
        setError("");
        try {
            const data = await fetchClosed({ page, perPage });
            setRows(Array.isArray(data?.data) ? data.data : []);
            setMeta(data?.meta ?? { total: 0, per_page: perPage, current_page: page });
        } catch (e) {
            setError(e?.message || "Failed to load closed data");
        } finally {
            setLoading(false);
        }
    }, [meta.current_page, meta.per_page]);

    useEffect(() => {
        load({ page: meta.current_page, perPage: meta.per_page });
    }, [load, meta.current_page, meta.per_page]);

    return (
        <Card className="card-shadow">
            <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
                    <div>
                        <div className="table-title">Closed</div>
                        <div className="text-muted small">Completed positions from `/api/closed`</div>
                    </div>
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => load({ page: meta.current_page, perPage: meta.per_page })}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" /> Refreshing
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-rotate me-2" />
                                Refresh
                            </>
                        )}
                    </Button>
                </div>

                {error ? (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                ) : null}

                <DataTable
                    columns={columns}
                    data={rows}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={Number(meta?.total || 0)}
                    paginationPerPage={Number(meta?.per_page || 10)}
                    paginationDefaultPage={Number(meta?.current_page || 1)}
                    onChangePage={(page) => load({ page, perPage: meta.per_page })}
                    onChangeRowsPerPage={(perPage, page) => load({ page, perPage })}
                    highlightOnHover
                    dense
                    persistTableHead
                    noDataComponent={<div className="py-4 text-muted">No closed rows</div>}
                />
            </Card.Body>
        </Card>
    );
}

